import { ANIMAL_TYPES_DISPLAY_TO_API_MAP } from '../../core/scripts/types/animal-types';

const searchAnimalQuery = `query SearchAnimal($pagination: PaginationInfoInput!, $filters: AnimalSearchFiltersInput!)
 { searchAnimal(pagination: $pagination
   sort: { field: "animal_type", order: "desc" } filters: $filters) { 
totalCount
animals { 
    animalId 
    animalName
    primaryPhotoId
    
    physical {
       size {
        label
       }
       breed {
         primary
         mixed
       }
       age { label value }
    } 
    _contact {
        address {
          city
          state
        }
      }
      
      publicUrl {
        url
      }
    
    
 } } }`;
const animalAttributesQuery = `query AllAnimalAttributes($animalType: String!) {
  allAnimalAttributes(animalType: $animalType) {
      ages {
      id
      alternateId
      name
    }
  
    houseTrainedList {
      id
      alternateId
      name
    }
  
    interactionsList {
      id
      alternateId
      name
    }
    interactions {
      cats {
        id
        alternateId
        name
      }
      children8AndUp {
        id
        alternateId
        name
      }
      childrenUnder8 {
        id
        alternateId
        name
      }
      dogs {
        id
        alternateId
        name
      }
      
    }
   
    sexes {
      id
      alternateId
      name
    }
    sizes {
      id
      alternateId
      name
    }
   
    specialNeeds
    speciesList {
   
      primaryBreeds {
        id
        alternateId
        name
      }
      secondaryBreeds {
        id
        alternateId
        name
      }
    }
  }
}`

const getOrganizationQuery = `query GetOrganization($organizationId: String!, $idType: String!) {
    organization(id: $organizationId, idType: $idType) {
        organizationName
        displayId
        organizationId
    }
}`;

const typenameQuery = `query { __typename }`;

export default class Client {

    constructor(apiBase) {
        this.apiBase = apiBase;

    }

    /**
     * Check if a string is a UUID format (organization ID)
     * UUIDs are 36 characters with hyphens at positions 8, 13, 18, 23
     * @param {string} id - The ID to check
     * @returns {boolean} - True if the ID appears to be a UUID
     */
    isUUID(id) {
        if (!id || typeof id !== 'string') {
            return false;
        }
        // UUID regex pattern: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        return uuidRegex.test(id);
    }


    async fetchOrganization(displayId) {
        try {
            const res = await fetch(this.apiBase, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    query: getOrganizationQuery,
                    variables: {
                        organizationId: displayId,
                        idType: 'display_id'
                    }
                })
            });

            if (!res.ok) {
                throw new Error(`HTTP error! status: ${res.status}`);
            }

            const data = await res.json();
            return data?.data?.organization?.organizationId || null;
        } catch (err) {
            console.error('fetchOrganization error:', err);
            return null;
        }
    }

    async fetchOrganizations(displayIds) {
        try {
            const organizationPromises = displayIds.map(displayId => 
                this.fetchOrganization(displayId)
            );
            
            const organizationIds = await Promise.all(organizationPromises);
            
            // Filter out any null values (failed lookups)
            return organizationIds.filter(id => id !== null);
        } catch (err) {
            console.error('fetchOrganizations error:', err);
            return [];
        }
    }

    async fetchAnimals(filters, pageNumber) {
        const filterVariables = await this.transformFilterToGqlVariables(filters)

        try {
            const res = await fetch(this.apiBase, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body:JSON.stringify({query: searchAnimalQuery, variables: {
                    isConsumer: true,
                    filters: filterVariables,
                        pagination: {
                            fromPage: pageNumber - 1,
                            pageSize: filters.limit,
                        }
                    }})
            });

            if (!res.ok) {
                throw new Error(`HTTP error! status: ${res.status}`);
            }

            const data = await res.json();
            return data && data.data ? data.data.searchAnimal : data;
        } catch (err) {
            console.error('fetchAnimals error:', err);
            throw err;
        }
    }

    async animalData(animalType) {
        try {
            const res = await fetch(this.apiBase, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({query: animalAttributesQuery, variables: {animalType}})
            })
            const jsonData = await res.json();
            return jsonData && jsonData?.data?.allAnimalAttributes ? jsonData?.data.allAnimalAttributes : null
        } catch (err) {
            throw err;
        }
    }

    async transformFilterToGqlVariables(filters) {
        const variables = {}
        if (filters?.type) {
            // Convert single plural display name to array with single singular API key
            const convertedType = ANIMAL_TYPES_DISPLAY_TO_API_MAP[filters.type] || filters.type.toLowerCase();
            variables['animal_type'] = [convertedType];
        }
        if (filters?.breed) {
            variables['breeds'] = filters.breed
        }
        if (filters?.size) {
            variables.size = filters.size
        }
        if (filters?.gender) {
            variables.sex = filters.gender
        }
        if (filters?.age) {
            variables.age = filters.age
        }
        if (filters?.good_with_children) {
            variables['children_8_and_up'] = true
            variables['children_under_8'] = true
        }
        if (filters?.good_with_dogs) {
            variables['dogs'] = true
        }
        if (filters?.good_with_cats) {
            variables['cats'] = true
        }
        if (filters?.declawed) {
            variables['declawed'] = true
        }
        if (filters?.house_trained) {
            variables['house_trained'] = true
        }
        if (filters?.special_needs) {
            variables['special_needs'] = true
        }
        if (filters.status) {
           variables['adoption_status'] = filters.status
        }
        if (filters.organization && filters.organization.length > 0) {
            // Separate UUIDs (organization IDs) from display IDs
            const organizationIds = [];
            const displayIds = [];
            
            filters.organization.forEach(id => {
                if (this.isUUID(id)) {
                    // This is already an organization ID (UUID), use it directly
                    organizationIds.push(id);
                } else {
                    // This is a display ID, needs conversion
                    displayIds.push(id);
                }
            });
            
            // Convert any display IDs to organization IDs
            if (displayIds.length > 0) {
                const convertedIds = await this.fetchOrganizations(displayIds);
                organizationIds.push(...convertedIds);
            }
            
            if (organizationIds.length > 0) {
                variables['organization_id'] = organizationIds;
            }
        }

        return variables;
    }
}
